/**
 * VPE Dashboard Service
 * Handles all dashboard-related operations including group management
 * Replaces hardcoded API calls from VPEDashboard.tsx
 */

import { apiClient } from '@/services/core/apiClient';
import { groupsGraphqlService, patientsGraphqlService } from '@/services/api';

export interface Group {
  group_id: number;
  group_name: string;
  group_type: string;
  clinic_name: string;
  es_name?: string;
  is_archive: boolean;
}

export interface Patient {
  id: number;
  name: string;
  emr: string;
  dob: string;
  mobile: string;
  home: string;
  fullString: string;
}

export interface DashboardStats {
  totalGroups: number;
  activeGroups: number;
  totalPatients: number;
  totalVPEs: number;
}

export class VPEDashboardService {
  private userId: string | number | null = null;
  private token: string | null = null;
  private hasuraToken: string | null = null;

  /**
   * Update service context with user information
   */
  updateContext(context: { userId: string | number; token: string; hasuraToken?: string }) {
    this.userId = context.userId;
    this.token = context.token;
    this.hasuraToken = context.hasuraToken || null;
  }

  /**
   * Archive a group
   */
  async archiveGroup(groupId: number): Promise<{ success: boolean; message?: string }> {
    if (!this.userId || !this.token) {
      throw new Error('User authentication required');
    }

    const endpoint = `/api/pep/${this.userId}/archiveGroup/${groupId}`;
    
    try {
      const response = await apiClient.put(endpoint, {
        to_archive: true
      }, this.token);

      if (!response.success) {
        throw new Error(response.error || 'Failed to archive group');
      }

      return response.data;
    } catch (error) {
      console.error('Error archiving group:', error);
      throw new Error('Failed to archive group');
    }
  }

  /**
   * Delete a group
   */
  async deleteGroup(groupId: number): Promise<{ success: boolean; message?: string }> {
    if (!this.userId || !this.token) {
      throw new Error('User authentication required');
    }

    const endpoint = `/api/pep/${this.userId}/ga/${groupId}`;
    
    try {
      const response = await apiClient.delete(endpoint, this.token);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete group');
      }

      return response.data;
    } catch (error) {
      console.error('Error deleting group:', error);
      throw new Error('Failed to delete group');
    }
  }

  /**
   * Search patients (mock implementation for now)
   */
  async searchPatients(searchTerm: string, searchBy: string = 'name'): Promise<Patient[]> {
    try {
      // Mock patient data for now - replace with actual API call when available
      const mockPatients: Patient[] = [
        {
          id: 1,
          name: "ZZZAleyamma Sharma",
          emr: "4080",
          dob: "30/09/1948",
          mobile: "4808622020",
          home: "4806593516",
          fullString: "ZZZAleyamma Sharma | EMR: 4080 | DOB: 30/09/1948 | Mobile Number: 4808622020 | Home Number: 4806593516 | Full Name (without middle name): ZZZAleyamma Sharma"
        }
      ];

      // Filter based on search term and type
      return mockPatients.filter(patient => {
        const searchLower = searchTerm.toLowerCase();
        switch (searchBy) {
          case 'name':
            return patient.name.toLowerCase().includes(searchLower);
          case 'emr':
            return patient.emr.includes(searchTerm);
          case 'mobile':
            return patient.mobile.includes(searchTerm);
          default:
            return patient.fullString.toLowerCase().includes(searchLower);
        }
      });
    } catch (error) {
      console.error('Error searching patients:', error);
      throw new Error('Failed to search patients');
    }
  }

  /**
   * Add patient to group
   */
  async addPatientToGroup(patient: Patient, clinicId: number = 2): Promise<{ success: boolean; message?: string }> {
    if (!this.hasuraToken) {
      throw new Error('Hasura authentication required for GraphQL operations');
    }

    const query = `
      query MyQuery($clinicID: Int!, $searchStr: String!) {
        pep_vw_consolidated_data(
          where: {
            ccm_status: {_nin: [1,2,3,7,8,9,10]},
            clinic_id: {_eq: $clinicID},
            search_string: {_eq: $searchStr}
          }
        ) {
          patient_clinic_data_id
        }
      }
    `;

    try {
      // Use the consolidated data query with proper filtering - FIXED: Use hasuraToken for GraphQL
      const result = await patientsGraphqlService.getConsolidatedData(
        this.hasuraToken!,
        {
          ccm_status: { _nin: [1, 2, 3, 7, 8, 9, 10] },
          clinic_id: { _eq: clinicId },
          search_string: { _eq: patient.fullString }
        }
      );

      if (result.data?.pep_vw_consolidated_data && result.data.pep_vw_consolidated_data.length > 0) {
        return { success: true, message: 'Patient added successfully' };
      } else {
        throw new Error('Failed to add patient');
      }
    } catch (error) {
      console.error('Error adding patient to group:', error);
      throw new Error('Failed to add patient to group');
    }
  }

  /**
   * Filter groups based on search criteria
   */
  filterGroups(groups: Group[], searchTerm: string, searchBy: string): Group[] {
    if (!searchTerm.trim()) {
      return groups;
    }

    const searchLower = searchTerm.toLowerCase();
    return groups.filter(group => {
      switch (searchBy) {
        case 'group_name':
          return group.group_name.toLowerCase().includes(searchLower);
        case 'group_id':
          return group.group_id.toString().includes(searchLower);
        case 'group_type':
          return group.group_type.toLowerCase().includes(searchLower);
        case 'clinic_name':
          return group.clinic_name.toLowerCase().includes(searchLower);
        case 'assigned_vpe':
          return (group.es_name && group.es_name.toLowerCase().includes(searchLower));
        case 'all':
        default:
          return (
            group.group_name.toLowerCase().includes(searchLower) ||
            group.clinic_name.toLowerCase().includes(searchLower) ||
            group.group_type.toLowerCase().includes(searchLower) ||
            group.group_id.toString().includes(searchLower) ||
            (group.es_name && group.es_name.toLowerCase().includes(searchLower))
          );
      }
    });
  }

  /**
   * Calculate dashboard statistics
   */
  calculateStats(groups: Group[]): DashboardStats {
    const totalGroups = groups.length;
    const activeGroups = groups.filter(g => !g.is_archive).length;
    const totalVPEs = new Set(groups.map(g => g.es_name).filter(Boolean)).size;
    
    // Mock patient count for now - replace with actual API call
    const totalPatients = 306;

    return {
      totalGroups,
      activeGroups,
      totalPatients,
      totalVPEs
    };
  }

  /**
   * Paginate groups
   */
  paginateGroups(groups: Group[], currentPage: number, rowsPerPage: number) {
    const totalPages = Math.ceil(groups.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedGroups = groups.slice(startIndex, startIndex + rowsPerPage);

    return {
      paginatedGroups,
      totalPages,
      startIndex,
      totalItems: groups.length
    };
  }
}

// Export singleton instance
export const vpeDashboardService = new VPEDashboardService();